/**
 * CommonSettings - общие настройки секции (padding, background, visibility)
 * Одинаковые для всех секций
 */
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PRESET_COLORS = [
  { value: '#FFFFFF', label: 'Белый' },
  { value: '#F9FAFB', label: 'Светло-серый' },
  { value: '#111827', label: 'Тёмный' },
  { value: '#1F2937', label: 'Графит' },
  { value: 'transparent', label: 'Прозрачный' },
];

const PADDING_PRESETS = [
  { value: 32, label: 'S' },
  { value: 64, label: 'M' },
  { value: 96, label: 'L' },
  { value: 128, label: 'XL' },
];

export default function CommonSettings({ section, onChange, category }) {
  // Hero секции используют min-height, padding не имеет смысла
  const isHeroSection = category === 'hero';
  const [showAdvanced, setShowAdvanced] = useState(false);

  const styles = section?.styles || {};
  const visibility = section?.visibility || { mobile: true, tablet: true, desktop: true };

  const updateStyles = (updates) => {
    onChange({
      ...section,
      styles: { ...styles, ...updates }
    });
  };

  const updateVisibility = (device, value) => {
    onChange({
      ...section,
      visibility: { ...visibility, [device]: value }
    });
  };

  const paddingTop = styles.paddingTop ?? 64;
  const paddingBottom = styles.paddingBottom ?? 64;
  const backgroundColor = styles.backgroundColor || '#FFFFFF';

  return (
    <div className="space-y-4">
      {/* Отступы */}
      <div>
        <label className="block text-sm font-medium mb-2">Отступы</label>
        <div className={`grid gap-3 ${isHeroSection ? 'grid-cols-1' : 'grid-cols-2'}`}>
          <div>
            <span className="text-xs text-text-secondary mb-1 block">Сверху</span>
            <div className="flex gap-1">
              {PADDING_PRESETS.map(preset => (
                <button
                  key={preset.value}
                  onClick={() => updateStyles({ paddingTop: preset.value })}
                  className={`flex-1 py-1.5 text-xs rounded transition-colors ${
                    paddingTop === preset.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                  type="button"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
          {/* Отступ снизу не показываем для Hero (они используют min-height) */}
          {!isHeroSection && (
            <div>
              <span className="text-xs text-text-secondary mb-1 block">Снизу</span>
              <div className="flex gap-1">
                {PADDING_PRESETS.map(preset => (
                  <button
                    key={preset.value}
                    onClick={() => updateStyles({ paddingBottom: preset.value })}
                    className={`flex-1 py-1.5 text-xs rounded transition-colors ${
                      paddingBottom === preset.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                    type="button"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Цвет фона */}
      <div>
        <label className="block text-sm font-medium mb-2">Фон</label>
        <div className="flex gap-2">
          {PRESET_COLORS.map(color => (
            <button
              key={color.value}
              onClick={() => updateStyles({ backgroundColor: color.value })}
              className={`w-8 h-8 rounded-lg border-2 transition-all ${
                backgroundColor === color.value
                  ? 'border-primary scale-110'
                  : 'border-border hover:border-primary/50'
              }`}
              style={{
                backgroundColor: color.value === 'transparent' ? undefined : color.value,
                backgroundImage: color.value === 'transparent'
                  ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
                  : undefined,
                backgroundSize: '8px 8px',
                backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
              }}
              title={color.label}
              type="button"
            />
          ))}
          <div className="relative">
            <input
              type="color"
              value={backgroundColor === 'transparent' ? '#ffffff' : backgroundColor}
              onChange={(e) => updateStyles({ backgroundColor: e.target.value })}
              className="w-8 h-8 rounded-lg border-2 border-border cursor-pointer"
              title="Свой цвет"
            />
          </div>
        </div>
      </div>

      {/* Расширенные настройки */}
      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-1 text-xs text-text-secondary hover:text-foreground transition-colors"
        type="button"
      >
        <Icon name={showAdvanced ? 'ChevronUp' : 'ChevronDown'} size={14} />
        Дополнительно
      </button>

      {showAdvanced && (
        <div className="space-y-3 pt-2">
          {/* Видимость на устройствах */}
          <div>
            <span className="text-xs text-text-secondary mb-2 block">Показывать на</span>
            <div className="flex gap-2">
              {[
                { key: 'mobile', icon: 'Smartphone', label: 'Телефон' },
                { key: 'tablet', icon: 'Tablet', label: 'Планшет' },
                { key: 'desktop', icon: 'Monitor', label: 'Десктоп' },
              ].map(device => (
                <button
                  key={device.key}
                  onClick={() => updateVisibility(device.key, !visibility[device.key])}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border transition-colors ${
                    visibility[device.key] !== false
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-text-secondary hover:border-primary/50'
                  }`}
                  title={device.label}
                  type="button"
                >
                  <Icon name={device.icon} size={16} />
                </button>
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
